<template>
    <div class="h-100 d-flex flex-column">
        <div v-if="loading" class="text-center p-5">
            <b-spinner variant="primary" label="Loading..."></b-spinner>
        </div>
        <div v-else-if="detailData" class="flex-grow-1" :class="{'overflow-hidden': !isMobile}">
            <div class="row m-0" :class="{'h-100': !isMobile}">
                <!-- Left Column: Chart & Info -->
                <div class="col-12 col-md-7 p-0 d-flex flex-column order-2 order-md-1" :class="{'h-100 border-right overflow-auto': !isMobile}">
                    <!-- 1. K-Line Chart Area -->
                    <div class="kline-container bg-card border-bottom position-relative" style="min-height: 400px; flex: 1;">
                        <!-- Chart Header (Stock Info) -->
                        <div class="position-absolute p-2 d-flex justify-content-between w-100" style="z-index: 10; background: var(--bg-card); opacity: 0.85;">
                            <div @click="openStockSidebar" style="cursor: pointer;">
                                <span class="font-weight-bold mr-2 text-main">{{ stock ? stock.stock_name : '' }}</span>
                                <span class="small text-muted">{{ stock ? stock.stock_code : '' }}</span>
                            </div>

                            <div>
                                <span :class="getColorClass(detailData.detail.quote_change)" class="font-weight-bold">{{ detailData.detail.cur_price }}</span>
                                <span :class="getColorClass(detailData.detail.quote_change)" class="small ml-1">{{ detailData.detail.quote_change }}%</span>
                            </div>
                        </div>
                        
                        <stock-kline-day v-if="stock" :symbol_code="stock.stock_code" chart_height="100%" :title="''" :subText="''" :showLhb="true" />
                    </div>

                    <!-- 2. Statistics Bar -->
                    <div class="d-flex bg-card border-bottom text-center small py-2 flex-shrink-0">
                        <div class="flex-fill border-right">
                            <div class="text-muted mb-1">换手率</div>
                            <div class="text-main">{{ detailData.detail.turnover_ratio }}%</div>
                        </div>
                        <div class="flex-fill border-right">
                            <div class="text-muted mb-1">成交额</div>
                            <div class="text-main">{{ formatMoney(detailData.detail.turnover) }}</div>
                        </div>
                        <div class="flex-fill border-right">
                            <div class="text-muted mb-1">净买入</div>
                            <div :class="getColorClass(detailData.detail.buy_in)">{{ formatMoney(detailData.detail.buy_in) }}</div>
                        </div>
                        <div class="flex-fill">
                            <div class="text-muted mb-1">上榜数</div>
                            <div class="text-main">{{ detailData.detail.join_num }}</div>
                        </div>
                    </div>
                    
                    <!-- 3. Reason Section -->
                    <div class="p-2 bg-muted border-bottom small text-muted flex-shrink-0">
                        <span class="font-weight-bold text-main mr-1">上榜理由:</span> {{ detailData.detail.up_reason }}
                    </div>
                </div>

                <!-- Right Column: Seat Lists -->
                <div class="col-12 col-md-5 p-0 bg-muted order-1 order-md-2" :class="{'h-100 overflow-auto': !isMobile}">
                    <div v-for="(group, gIdx) in traderGroups" :key="'g'+gIdx">
                        <!-- Group Header (Reason) -->
                        <div class="p-2 bg-muted border-bottom small font-weight-bold text-main border-top" v-if="traderGroups.length > 1">
                            {{ group.reason_label }}
                        </div>
                        
                        <div class="row m-0">
                            <!-- Buy List -->
                            <div class="col-12 col-md-12 p-0 border-bottom">
                                <div class="p-2 border-bottom small font-weight-bold text-muted d-flex justify-content-between" style="background-color: var(--bg-muted);">
                                    <span>买入前五营业部</span>
                                    <span class="text-danger">总计: {{ formatMoney(group.buy_total) }} ({{ calcPct(group.buy_total) }})</span>
                                </div>
                                <div v-for="(trader, idx) in group.buy" :key="'buy'+gIdx+idx" class="d-flex p-2 border-bottom align-items-center bg-card">

                                    <!-- Rank Badge -->
                                    <div class="mr-2">
                                        <span class="badge badge-danger text-white d-flex align-items-center justify-content-center" style="width: 18px; height: 18px; padding: 0;">{{ idx + 1 }}</span>
                                    </div>
                                    <!-- Name & Tags -->
                                    <div style="flex: 1; min-width: 0;">
                                        <div class="d-flex align-items-center mb-1">
                                            <span class="font-weight-bold text-main small text-truncate" style="cursor: pointer;" @click="openHistory(trader)">
                                                {{ trader.trader_name }}
                                            </span>
                                            <b-badge v-if="getYouziLabel(trader.youzi_icon)" variant="secondary" class="ml-1 scale-8 font-weight-normal text-white bg-secondary border-0 flex-shrink-0">
                                                {{ getYouziLabel(trader.youzi_icon) }}
                                            </b-badge>
                                            <template v-if="trader.group_icon">
                                                <span v-for="(icon, idx) in parseGroupIcon(trader.group_icon)" :key="'gi-buy'+idx" 
                                                    class="badge badge-danger text-white ml-1 scale-8 font-weight-normal flex-shrink-0">
                                                    {{ icon }}
                                                </span>
                                            </template>
                                        </div>
                                    </div>
                                    <!-- Amount -->
                                    <div class="text-right ml-2" style="min-width: 130px;">
                                        <div class="text-up font-weight-bold">
                                            {{ formatMoney(trader.buy_amount) }}
                                            <span class="small ml-1">({{ calcPct(trader.buy_amount) }})</span>
                                        </div>
                                        <div v-if="trader.sell_amount > 0" class="text-down scale-8 small">
                                            卖:{{ formatMoney(trader.sell_amount) }}
                                            <span class="ml-1">({{ calcPct(trader.sell_amount) }})</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
            
                            <!-- Sell List -->
                            <div class="col-12 col-md-12 p-0">
                                <div class="p-2 border-bottom small font-weight-bold text-muted d-flex justify-content-between" style="background-color: var(--bg-muted);">
                                    <span>卖出前五营业部</span>
                                    <span class="text-down">总计: {{ formatMoney(group.sell_total) }} ({{ calcPct(group.sell_total) }})</span>
                                </div>
                                <div v-for="(trader, idx) in group.sell" :key="'sell'+gIdx+idx" class="d-flex p-2 border-bottom align-items-center bg-card">
                                    <!-- Rank Badge -->
                                    <div class="mr-2">
                                        <span class="badge badge-success text-white d-flex align-items-center justify-content-center" style="width: 18px; height: 18px; padding: 0;">{{ idx + 1 }}</span>
                                    </div>
                                    <!-- Name & Tags -->
                                    <div style="flex: 1; min-width: 0;">
                                        <div class="d-flex align-items-center mb-1">
                                            <span class="font-weight-bold text-main small text-truncate" style="cursor: pointer;" @click="openHistory(trader)">
                                                {{ trader.trader_name }}
                                            </span>
                                            <b-badge v-if="getYouziLabel(trader.youzi_icon)" variant="secondary" class="ml-1 scale-8 font-weight-normal text-white bg-secondary border-0 flex-shrink-0">
                                                {{ getYouziLabel(trader.youzi_icon) }}
                                            </b-badge>
                                            <template v-if="trader.group_icon">
                                                <span v-for="(icon, idx) in parseGroupIcon(trader.group_icon)" :key="'gi-sell'+idx" 
                                                    class="badge badge-success text-white ml-1 scale-8 font-weight-normal flex-shrink-0">
                                                    {{ icon }}
                                                </span>
                                            </template>
                                        </div>
                                    </div>
                                    <!-- Amount -->
                                    <div class="text-right ml-2" style="min-width: 130px;">
                                        <div class="text-down font-weight-bold">
                                            {{ formatMoney(trader.sell_amount) }}
                                            <span class="small ml-1">({{ calcPct(trader.sell_amount) }})</span>
                                        </div>
                                        <div v-if="trader.buy_amount > 0" class="text-up scale-8 small">
                                            买:{{ formatMoney(trader.buy_amount) }}
                                            <span class="ml-1">({{ calcPct(trader.buy_amount) }})</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="text-center p-5 text-muted">
            请选择股票查看详情
        </div>
        <stock-info-sidebar 
            v-if="stock" 
            :symbol_code="stock.stock_code" 
            :symbol_name="stock.stock_name" 
            :date_mark="date">
        </stock-info-sidebar>
        
        <b-modal v-model="historyModal" size="xl" hide-footer centered>
            <template #modal-title>
                {{ currentTrader }}
                <b-badge v-if="currentTraderYouziIcon" variant="secondary" class="ml-2 scale-8 font-weight-normal text-white bg-secondary border-0">
                    {{ currentTraderYouziIcon }}
                </b-badge>
                <b-badge v-for="(icon, idx) in currentTraderGroupIcons" :key="idx" variant="danger" class="ml-1 scale-8 font-weight-normal text-white border-0">
                    {{ icon }}
                </b-badge>
                <span class="small ml-2 text-muted">- 历史记录</span>
            </template>
            <div class="row m-0" :style="{ height: '75vh' }">
                <!-- K-Line Chart Area (Left) -->
                <div class="col-12 col-md-5 p-0 d-flex flex-column" :class="{'h-100 border-right': !isMobile, 'border-bottom': isMobile}" :style="{ height: isMobile ? '50%' : '100%' }">
                    <div v-if="currentHistoryStockCode" class="h-100 w-100">
                        <stock-kline-day 
                            :key="currentHistoryStockCode + currentTrader"
                            :symbol_code="currentHistoryStockCode" 
                            chart_height="100%" 
                            :title="currentHistoryStockName" 
                            :subText="currentHistoryStockCode" 
                            :showLhb="true" 
                            :traderName="currentTrader"
                            :highlightDate="currentHistoryDate"
                        />
                    </div>
                    <div v-else class="h-100 d-flex align-items-center justify-content-center text-muted">
                        请选择一条记录查看K线
                    </div>
                </div>
                
                <!-- History List (Right) -->
                <div class="col-12 col-md-7 p-0 d-flex flex-column position-relative" :class="{'h-100': !isMobile}" :style="{ height: isMobile ? '45%' : '100%' }">
                    <div class="p-2 border-bottom d-flex align-items-center justify-content-end"
                         :class="{'position-absolute w-100': isMobile, 'bg-white': !isMobile}"
                         :style="{ backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.7)' : '', zIndex: 10, top: 0, right: 0 }">
                        <b-form-checkbox v-model="filterByStock" switch size="sm" class="mr-2">
                            只看图表股票
                        </b-form-checkbox>
                    </div>
                    <div class="table-responsive flex-grow-1" :style="{ overflowY: 'auto', paddingTop: isMobile ? '45px' : '0' }" @scroll="onHistoryScroll">
                        <table class="table table-sm table-hover small" style="font-size: 0.8rem;">
                            <thead>
                                <tr class="bg-light text-secondary">
                                    <th style="width: 85px;">日期</th>
                                    <th>股票名称</th>
                                    <th>交易席位</th>
                                    <th>概念</th>
                                    <th class="text-right">涨幅</th>
                                    <th class="text-right">净买入</th>
                                    <th class="text-right">成交额</th>
                                    <th class="text-right">总市值</th>
                                    <th class="text-right">流通市值</th>
                                    <th class="text-right">振幅</th>
                                    <th class="text-right">换手率</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="historyLoading && historyPage === 1">
                                    <td colspan="11" class="text-center p-4">
                                        <b-spinner small></b-spinner>
                                    </td>
                                </tr>
                                <tr v-else-if="historyList.length === 0">
                                    <td colspan="11" class="text-center p-4 text-muted">暂无数据</td>
                                </tr>
                                <tr v-for="(item, idx) in historyList" :key="idx">
                                    <td @click="onHistoryDateClick(item)" style="cursor: pointer;" title="点击标记日期">{{ item.date }}</td>
                                    <td @click="updateHistoryChart(item)" style="cursor: pointer;" title="点击查看K线">
                                        <div class="d-flex align-items-center flex-wrap">
                                            <span class="font-weight-bold mr-1 text-primary">{{ item.stock_name }}</span>
                                            <span v-if="item.t_type === 1" class="badge badge-danger text-white scale-8 px-1 mr-1" style="line-height: 1;">T</span>
                                            <span v-if="item.t_icon" class="badge badge-danger text-white scale-8 px-1" style="line-height: 1;">{{ item.t_icon }}T</span>
                                        </div>
                                        <div class="d-flex align-items-center flex-wrap text-muted scale-8" style="transform-origin: left top;">
                                            <span class="mr-1">{{ item.stock_code }}</span>
                                            <span v-if="item.d3 > 0" class="text-danger border border-danger small px-1 scale-8 mr-1" style="line-height: 1; border-radius: 2px;">3日</span>
                                        </div>
                                    </td>
                                    <td>
                                    <div class="d-flex flex-column align-items-start justify-content-center" style="gap: 2px;">
                                        <span v-for="(icon, iidx) in item.buy_group_icons" :key="'buy'+iidx" 
                                            class="badge bg-white text-danger border-danger small px-1 scale-8 mb-1" 
                                            style="border-width: 1px; border-style: solid; max-width: 100%; height: auto;">
                                            <span>{{ icon.name }}</span>
                                            <span class="ml-1" style="opacity: 0.8;">+{{ formatMoney(icon.amount) }}</span>
                                        </span>
                                        <span v-for="(icon, iidx) in item.sell_group_icons" :key="'sell'+iidx" 
                                            class="badge bg-white text-success border-success small px-1 scale-8 mb-1" 
                                            style="border-width: 1px; border-style: solid; max-width: 100%; height: auto;">
                                            <span>{{ icon.name }}</span>
                                            <span class="ml-1" style="opacity: 0.8;">-{{ formatMoney(icon.amount) }}</span>
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <div style="max-width: 150px; white-space: normal;" :title="getDisplayConcepts(item.concepts).join(',')">
                                        <span v-for="(concept, cidx) in getDisplayConcepts(item.concepts).slice(0, 3)" :key="cidx" class="badge badge-light border text-secondary mr-1 mb-1">
                                            {{ concept }}
                                        </span>
                                        <span v-if="getDisplayConcepts(item.concepts).length > 3" class="text-muted small">...</span>
                                    </div>
                                </td>
                                <td class="text-right" :class="getColorClass(item.quote_change)">{{ item.quote_change }}%</td>
                                <td class="text-right" :class="getColorClass(item.buy_in)">{{ formatMoney(item.buy_in) }}</td>
                                <td class="text-right">{{ formatMoney(item.turnover) }}</td>
                                <td class="text-right">{{ formatMoney(item.capitalization) }}</td>
                                <td class="text-right">{{ formatMoney(item.circ_price) }}</td>
                                <td class="text-right">{{ item.amplitude }}%</td>
                                <td class="text-right">{{ item.turnover_ratio }}%</td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-if="historyLoading && historyPage > 1" class="text-center p-2 text-muted small">
                            <b-spinner small class="mr-1"></b-spinner>加载中...
                        </div>
                        <div v-else-if="historyList.length > 0 && historyList.length >= historyTotal" class="text-center p-2 text-muted small">
                            -- 到底了 --
                        </div>
                    </div>
                </div>
            </div>
            <!-- Pagination Removed -->
        </b-modal>
    </div>
</template>

<script>
import StockKlineDay from '@/components/stock/StockKlineDay.vue'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar.vue'
import { request_zzquant } from '@/network/request'

export default {
    name: 'LhbDetailContent',
    components: { StockKlineDay, StockInfoSidebar },
    props: ['detailData', 'loading', 'stock', 'date', 'isMobile'],
    data() {
        return {
            historyModal: false,
            historyList: [],
            historyTotal: 0,
            historyPage: 1,
            historyPerPage: 10,
            historyLoading: false,
            currentTrader: '',
            currentTraderId: '',
            currentTraderYouziIcon: '',
            currentTraderGroupIcons: [],
            currentHistoryStockCode: '',
            currentHistoryStockName: '',
            currentHistoryDate: '',
            filterByStock: false,
        }
    },
    watch: {
        filterByStock() {
            this.historyPage = 1
            this.historyList = []
            this.fetchHistory()
        }
    },
    computed: {
        traderGroups() {
            if (!this.detailData || !this.detailData.traders) return []
            
            // Group by reason_type
            const groups = {}
            this.detailData.traders.forEach(t => {
                const rt = t.reason_type || 'default'
                if (!groups[rt]) {
                    groups[rt] = { reason_type: rt, buy: [], sell: [] }
                }
                if (t.type === 1) { // Buy
                     groups[rt].buy.push(t)
                } else if (t.type === 2) { // Sell
                     groups[rt].sell.push(t)
                }
            })
            
            // Parse up_reason
            const reasonStr = this.detailData.detail.up_reason || ''
            const reasons = reasonStr.split(/[,，]/).filter(r => r && r.trim().length > 0)
            
            // Convert to array
            const result = Object.values(groups).map(g => {
                // Sort by rank
                g.buy.sort((a, b) => a.rank - b.rank)
                g.sell.sort((a, b) => a.rank - b.rank)
                
                // Calculate Top 5 Total
                g.buy_total = g.buy.reduce((sum, t) => sum + (t.buy_amount || 0), 0)
                g.sell_total = g.sell.reduce((sum, t) => sum + (t.sell_amount || 0), 0)
                
                // Assign label
                if (g.reason_type !== 'default') {
                    const idx = parseInt(g.reason_type)
                    if (!isNaN(idx) && idx < reasons.length) {
                         g.reason_label = reasons[idx]
                    } else {
                         g.reason_label = `上榜原因 ${g.reason_type}`
                    }
                } else {
                    g.reason_label = reasonStr
                }
                
                return g
            })
            
            // Sort groups: 0, 1, ...
            result.sort((a, b) => {
                 if (a.reason_type === 'default') return -1
                 if (b.reason_type === 'default') return 1
                 // Try numeric sort
                 const na = parseInt(a.reason_type)
                 const nb = parseInt(b.reason_type)
                 if (!isNaN(na) && !isNaN(nb)) return na - nb
                 return a.reason_type.localeCompare(b.reason_type)
            })
            
            return result
        }
    },
    methods: {
        openHistory(trader) {
            this.currentTrader = trader.trader_name
            this.currentTraderId = trader.trader_id
            this.currentTraderYouziIcon = this.getYouziLabel(trader.youzi_icon)
            this.currentTraderGroupIcons = this.parseGroupIcon(trader.group_icon)
            this.currentHistoryDate = '' // Reset date highlight
            
            // Initialize chart with current stock
            if (this.stock) {
                this.currentHistoryStockCode = this.stock.stock_code
                this.currentHistoryStockName = this.stock.stock_name
            } else {
                this.currentHistoryStockCode = ''
                this.currentHistoryStockName = ''
            }

            this.historyPage = 1
            this.historyList = []
            this.historyModal = true
            this.fetchHistory()
        },
        updateHistoryChart(item) {
            if (item && item.stock_code) {
                this.currentHistoryStockCode = item.stock_code
                this.currentHistoryStockName = item.stock_name
            }
            if (item && item.date) {
                this.currentHistoryDate = item.date
            }
        },
        onHistoryDateClick(item) {
            if (item && item.date) {
                this.currentHistoryDate = item.date
                // Also ensure we are showing the correct stock if they click the date but the chart is on another stock?
                // The user requirement implies clicking the date on a row.
                // If the row corresponds to a different stock than displayed, we probably should switch the stock too?
                // "当我点击的股票这一行的第一列日期" -> "When I click the date of the first column of the stock row"
                // It implies the row is associated with a stock.
                
                // If the chart is currently showing a different stock, should we switch?
                // If filterByStock is true, then all rows are for the current stock.
                // If filterByStock is false, rows can be different stocks.
                
                if (item.stock_code && item.stock_code !== this.currentHistoryStockCode) {
                    this.currentHistoryStockCode = item.stock_code
                    this.currentHistoryStockName = item.stock_name
                }
            }
        },
        fetchHistory() {
            if (this.historyLoading) return
            this.historyLoading = true
            request_zzquant({
                url: '/market/lhb/trader/history',
                method: 'get',
                params: {
                    trader_name: this.currentTrader,
                    trader_id: this.currentTraderId,
                    page: this.historyPage,
                    per_page: this.historyPerPage,
                    stock_code: this.filterByStock ? this.currentHistoryStockCode : null
                }
            }).then(res => {
                this.historyLoading = false
                if (res && res.data && res.data.code === 20000) {
                    const newList = res.data.data.list || []
                    if (this.historyPage === 1) {
                        this.historyList = newList
                    } else {
                        this.historyList = this.historyList.concat(newList)
                    }
                    this.historyTotal = res.data.data.total
                }
            }).catch(err => {
                this.historyLoading = false
                console.error(err)
            })
        },
        onHistoryScroll(e) {
            const el = e.target
            // Trigger when near bottom (50px buffer)
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
                if (this.historyList.length < this.historyTotal && !this.historyLoading) {
                    this.historyPage += 1
                    this.fetchHistory()
                }
            }
        },
        getDisplayConcepts(conceptsStr) {
            if (!conceptsStr) return []
            return conceptsStr.split(',').map(s => s.split(':')[1] || s)
        },
        openStockSidebar() {
            this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
        },
        parseGroupIcon(iconStr) {
            if (!iconStr) return []
            // group_icon is comma-separated string like "1,2,3"
            return iconStr.split(',').filter(s => s.trim())
        },
        getYouziLabel(val) {
            if (val == 1) return '一线游资'
            if (val == 2) return '顶级游资'
            if (val == 3) return '知名游资'
            return null
        },
        getColorClass(val) {
            if (val > 0) return 'text-danger'
            if (val < 0) return 'text-success'
            return ''
        },
        formatMoney(val) {
            if (!val) return '0'
            let absVal = Math.abs(val)
            let res = ''
            if (absVal >= 100000000) {
                res = (absVal / 100000000).toFixed(2) + '亿'
            } else if (absVal >= 10000) {
                res = (absVal / 10000000).toFixed(2) + '千万'
            } else {
                res = absVal.toFixed(2)
            }
            if (val < 0) return '-' + res
            return res
        },
        calcPct(amount) {
            const turnover = (this.detailData && this.detailData.detail && this.detailData.detail.turnover) ? this.detailData.detail.turnover : 0
            if (!turnover || turnover <= 0) return '--'
            const pct = (Math.abs(amount) * 100) / turnover
            return pct.toFixed(2) + '%'
        }
    }
}
</script>

<style scoped>
.text-danger { color: #e33e33 !important; }
.text-success { color: #17803d !important; }
.badge-danger { background-color: #e33e33; }
.badge-success { background-color: #17803d; }
.scale-8 { transform: scale(0.85); transform-origin: left center; display: inline-block; }

@media (max-width: 768px) {
    .kline-container {
        min-height: 250px !important;
    }
}
</style>
